import React, { useMemo, useState, useEffect } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import AccountDetailsTab from './tabs/AccountDetailsTab';
import { translate } from 'components/Translation';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import VerificationTab from './tabs/VerificationTab';
import CustomConfirmationDialog from 'components/CustomConfirmationDialog/CustomConfirmationDialog';
import { BodyS } from 'components/Typography/Typography';
import { AccountsService } from 'services/accounts/accounts';
import styles from 'assets/jss/material-dashboard-pro-react/views/activeServices/addBillingAccountDialogStyle';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { AccountDetails } from 'utils/api/models/accounts/accountDetails';
import { PHONE_NUMBER_LENGTH } from 'utils/const/numbers';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAddBillingAccountDialogProps {
	isOpen: boolean;
	closeDialog: () => void;
	accounts: Array<SingleCorpAccount>;
}

const steps = {
	ACCOUNT_DETAILS: 0,
	VERIFICATION: 1,
};

interface IErrors {
	isOtpValid: boolean;
	accountExists: boolean;
	isBillingAccountValid: boolean;
	hasNumbers: boolean;
	success: boolean;
	verificationSent: boolean;
}

const AddBillingAccountDialog = ({ isOpen, closeDialog, accounts }: IAddBillingAccountDialogProps) => {
	const [billingAccountNumber, setBillingAccountNumber] = useState<string>('');
	const [billingAccountName, setBillingAccountName] = useState<string>('');
	const [otpValue, setOtpValue] = useState<string>('');
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [documentId, setDocumentId] = useState<string>('');
	const [confirmationDialogVisible, setConfirmationDialogVisible] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [fetchingOrganizationData, setFetchingOrganizationData] = useState<boolean>(false);
	const [contactNumberValid, setContactNumberValid] = useState<boolean>(true);
	const [showResendButton, setShowResendButton] = useState<boolean>(false);
	const [errors, setErrors] = useState<IErrors>({
		isOtpValid: true,
		isBillingAccountValid: true,
		hasNumbers: true,
		success: true,
		verificationSent: true,
		accountExists: false,
	});
	const [activeTab, setActiveTab] = useState<number>(steps.ACCOUNT_DETAILS);
	const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);
	const classes = useStyles();
	const dispatch = useDispatch();

	const isEnterprise: boolean = useMemo(() => accounts.length > 1, [accounts]);
	const isCorporate: boolean = useMemo(() => accounts.length === 1, [accounts]);
	const showContactNumberError: boolean = !loading && !contactNumberValid;
	const showErrorDialog: boolean = !contactNumberValid && isCorporate && isOpen;
	const showDialog: boolean = !showErrorDialog && isOpen;

	useEffect(() => {
		if (isCorporate) {
			const account = accounts[0];
			setSelectedAccount({
				id: account.accountId,
				name: account.accountName,
			});
		}
	}, [accounts, isCorporate]);

	useEffect(() => {
		if (selectedAccount) {
			setFetchingOrganizationData(true);
			AccountsService.getCorpAccountDetails(selectedAccount.id)
				.pipe(finalize(() => setFetchingOrganizationData(false)))
				.subscribe(({ accountDetails }: AccountDetails) => {
					setContactNumberValid(isContactNumberValid(accountDetails.contactNumber));
					setDocumentId(accountDetails?.documentId);
				});
		}
	}, [selectedAccount]);

	const clearDialog = (): void => {
		setBillingAccountNumber('');
		setBillingAccountName('');
		setOtpValue('');
		setActiveTab(steps.ACCOUNT_DETAILS);
		setShowResendButton(false);
		setContactNumberValid(true);
		if (isEnterprise) {
			setSelectedAccount(null);
		}
		clearErrors();
	};

	const clearErrors = (): void => {
		setErrors({
			isOtpValid: true,
			isBillingAccountValid: true,
			hasNumbers: true,
			success: true,
			verificationSent: true,
			accountExists: false,
		});
	};

	const handleError = (error: string): void => {
		if (activeTab === steps.ACCOUNT_DETAILS) {
			if (error === 'ERROR_ACCOUNT_EXISTS') {
				setErrors((prevErrors) => ({
					...prevErrors,
					accountExists: true,
				}));
			} else {
				setErrors((prevErrors) => ({
					...prevErrors,
					verificationSent: true,
				}));
			}
		} else {
			switch (error) {
				case 'ERROR_VALIDATING_OTP':
					setErrors((prevErrors) => ({
						...prevErrors,
						isOtpValid: false,
					}));
					setShowResendButton(true);
					break;
				case 'NO_ACCOUNTS_FOUND_FOR_ACCOUNT_ID':
					setErrors((prevErrors) => ({
						...prevErrors,
						hasNumbers: false,
					}));
					setActiveTab(steps.ACCOUNT_DETAILS);
					break;
				case 'INVALID_DOCUMENT':
					setErrors((prevErrors) => ({
						...prevErrors,
						isBillingAccountValid: false,
					}));
					setActiveTab(steps.ACCOUNT_DETAILS);
					break;
				default:
					setErrors((prevErrors) => ({
						...prevErrors,
						success: false,
					}));
					setShowResendButton(true);
			}
		}
	};

	const isContactNumberValid = (contactNumber: string): boolean => {
		const number = contactNumber.replace(/^\D+/g, '');
		return !!number && number.length === PHONE_NUMBER_LENGTH;
	};

	const nextAddDisabled: boolean = useMemo(() => {
		if (loading || showContactNumberError) {
			return true;
		}
		if (activeTab === steps.ACCOUNT_DETAILS) {
			return (isEnterprise && !selectedAccount) || billingAccountNumber.length === 0;
		}
		if (activeTab === steps.VERIFICATION) {
			return otpValue.length < 5;
		}
		return true;
	}, [activeTab, selectedAccount, billingAccountNumber, otpValue, isEnterprise, loading, showContactNumberError]);

	const handleClose = (): void => {
		closeDialog();
		clearDialog();
	};

	const handleCancelBack = (): void => {
		if (activeTab === steps.ACCOUNT_DETAILS) {
			handleClose();
		} else if (activeTab === steps.VERIFICATION) {
			setActiveTab(steps.ACCOUNT_DETAILS);
		}
	};

	const sendOtp = (): void => {
		setLoading(true);
		AccountsService.addAllAccountsForArborIdInitialize({
			accountId: billingAccountNumber,
			accountName: billingAccountName,
			orgAccountId: selectedAccount?.id || '',
		})
			.pipe(
				finalize(() => {
					setLoading(false);
				})
			)
			.subscribe(
				({ response }) => {
					const contactNumber = response?.addAllAccountsForArborIdInitializeResponse?.contactNumber;
					if (contactNumber) {
						setPhoneNumber(contactNumber);
					}
					if (activeTab !== steps.VERIFICATION) {
						setActiveTab(steps.VERIFICATION);
					}
					clearErrors();
				},
				({ response }) => handleError(response?.code)
			);
	};

	const addBillingAccount = (): void => {
		dispatch(showLoadingIndicator());
		AccountsService.addAllAccountsForArborIdOperation({
			accountId: billingAccountNumber,
			token: otpValue,
			orgAccountId: selectedAccount?.id || '',
			documentId,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					handleClose();
					setConfirmationDialogVisible(true);
				},
				({ response }) => handleError(response?.code)
			);
	};

	const handleNextAdd = (): void => {
		if (activeTab === steps.ACCOUNT_DETAILS) {
			sendOtp();
		} else if (activeTab === steps.VERIFICATION) {
			addBillingAccount();
		}
	};

	return (
		<>
			<CustomDialog
				isOpen={showDialog}
				hideDialog={handleClose}
				header={translate('active.services.add.billing.account')}
				rightSideButtons={[
					{
						text: activeTab === steps.ACCOUNT_DETAILS ? translate('cancel') : translate('back'),
						onClick: handleCancelBack,
						color: 'tertiary-light',
					},
					{
						text:
							activeTab === steps.ACCOUNT_DETAILS
								? translate('next')
								: translate('active.services.add.billing.account'),
						onClick: handleNextAdd,
						color: 'primary-light',
						disabled: nextAddDisabled,
					},
				]}
				dialogBody={
					<div className={classes.bodyWrapper}>
						<TabsWithDots
							tabs={[
								{
									name: translate('active.services.add.billing.account.account.details'),
									content: (
										<AccountDetailsTab
											isEnterprise={isEnterprise}
											billingAccountName={billingAccountName}
											setBillingAccountName={setBillingAccountName}
											billingAccountNumber={billingAccountNumber}
											setBillingAccountNumber={setBillingAccountNumber}
											selectedAccount={selectedAccount}
											setSelectedAccount={setSelectedAccount}
											accounts={accounts}
											loading={loading}
											fetchingOrganizationData={fetchingOrganizationData}
											showContactNumberError={showContactNumberError}
											showBillingAccountNumberError={!errors.isBillingAccountValid}
											showAccountExistsError={errors.accountExists}
											noNumbers={!errors.hasNumbers}
											verificationNotSent={!errors.verificationSent}
										/>
									),
								},
								{
									name: translate('active.services.add.billing.account.verification'),
									content: (
										<VerificationTab
											otpValue={otpValue}
											setOtpValue={setOtpValue}
											phoneNumber={phoneNumber}
											sendOtp={sendOtp}
											loading={loading}
											showOtpError={!errors.isOtpValid}
											showResendButton={showResendButton}
											failure={!errors.success}
										/>
									),
								},
							]}
							activeTab={activeTab}
							setActiveTab={setActiveTab}
						/>
					</div>
				}
			/>
			<CustomConfirmationDialog
				isOpen={confirmationDialogVisible}
				hideDialog={() => setConfirmationDialogVisible(false)}
				header={<BodyS>{translate('active.services.add.billing.account.request.sent')}</BodyS>}
			>
				<BodyS>{translate('active.services.add.billing.account.request.sent.additional.info')}</BodyS>
			</CustomConfirmationDialog>
			<CustomConfirmationDialog
				isOpen={showErrorDialog}
				hideDialog={closeDialog}
				isError
				header={<BodyS>{translate('active.services.add.billing.account.no.contact.number.assigned')}</BodyS>}
			>
				<BodyS>{translate('active.services.add.billing.account.please.contact.omantel')}</BodyS>
			</CustomConfirmationDialog>
		</>
	);
};

export default AddBillingAccountDialog;
